import * as React from "react";
import {cx} from "emotion";

import "./shipping-section.scss";

import {applyResultCart}     from "@Common/logic/checkout/common/apply-result-cart";
import {cs}                  from "@Common/react/chain-services";
import {consumeContext}      from "@Common/react/context";
import {keyed}               from "@Common/react/keyed";
import {Static2}             from "@Common/react/static-2";
import {UseState}            from "@Common/react/use-state";
import {ErrorHighlighter}    from "@Common/ui-components/light/error-highlighter";
import {scrollIntoView}      from "@Common/ui-components/light/scroll-into-view";
import {SliderCheckbox}      from "@Common/ui-components/slider-checkbox/slider-checkbox";
import {getNestedValue, snv} from "@Common/utils/arr-path";
import {findMinE}            from "@Common/utils/collections";
import {chain}               from "@Common/utils/fs";
import {omit}                from "@Common/utils/objects";

import {formatPhone}     from "../../../../common/formatPhone";
import {LoadingPanel}    from "../../../../common/loading-panel/loading-panel";
import {AGENT_LEVEL}     from "../../../../common/logic/user-level";
import {wrapSubmitting}  from "../../../../common/wrap-submitting";

import {AddressData}           from "../../../../common/shipping/address-data";
import {isFreightServiceLevel} from "./common/is-freight-service-level";
import {ShippingSplit}         from "./split/shipping-split";
import {ShippingTogether}      from "./together/shipping-together";

export const ShippingSection = ({store, autoCorrecting, next: rootNext}) => cs(
    consumeContext("apis"),
    consumeContext("userLevels"),
    consumeContext("company"),

    ["active", (_, next) => next(store.cart.items.find((i) => i.selections.distributionType === "shipping"))],

    ["addressData", (_, next) => AddressData({next})],

    ["apiError", (_, next) => UseState({next})],
    ["companySupportPhone", ({company}, next) => next(
        formatPhone(getNestedValue(company, ["companySupportPhone"]) || "(866) 977-3627")
    )],
    ["actionSectionErrors", ({companySupportPhone, apiError}, next) => next([
        store.cart.items.find((i) => (
            i.selections.distributionType === "shipping" &&
            !apiError.value &&
            (!i.shipping || !i.shipping.addressId || !i.shipping.serviceLevel)
        )) && (
            `Error Loading Shipping Data: Refresh page to try again. If the error continues, please call ${companySupportPhone} for assistance.`
        ),
    ].filter(v=>v))],
    ["breakingConsolidation", (_, next) => UseState({next})],
    ["eh", (_, next) => ErrorHighlighter({next})],
    ["domRef", (_, next) => Static2({next})],

    ["shippingTogether", ({addressData, apiError}, next) => ShippingTogether({
        addressData, store, autoCorrecting, apiError,
        next,
    })],
    ["shippingSplit"   , ({addressData, apiError}, next) => ShippingSplit({
        addressData, store, apiError,
        next,
    })],

    ["mode", ({shippingTogether, shippingSplit}, next) => next(
        !store.cart.shipping?.breakShippingConsolidation ? {
            name: "together",
            control: shippingTogether,
        } : {
            name: "split",
            control: shippingSplit,
        }
    )],

    ({breakingConsolidation, mode, active, actionSectionErrors, addressData, apiError, userLevels, eh, domRef, apis}) => rootNext({
        ready: !addressData.loading,

        invalid: active && (apiError.value || mode.control.invalid || actionSectionErrors.length),

        errorMessages: actionSectionErrors,

        highlightErrors: () => {
            eh.start();
            scrollIntoView(domRef.get());
        },

        autoCorrectAction: (cart) => syncShippingAddress(cart, {
            getDefaultAddress: () => addressData.officeAddress.value ?? addressData.myAddresses.value?.find(v=>v),
            apis, userLevels, apiError,
        }),

        render: () => active && (
            <div className={cx("shipping shipping-section-5y6", eh.css)} ref={domRef.set}>
                <div className="header">
                    <div className="title" xd-qa-type="label" xd-qa-name="Shipping">
                        Shipping
                    </div>
                    <div className="sub-title" xd-qa-type="label" xd-qa-name="Shipping Description">
                        Select delivery address and service level for your shopping cart
                    </div>
                    {isFreightServiceLevel(store.cart.shipping?.serviceLevel) ? (
                            <div className="shipping-info" xd-qa-type="label" xd-qa-name="freightShippingcost">
                                The Freight Shipping cost for this cart will be charged after checkout has been completed and the order(s) has shipped.
                            </div>
                        )
                        : (
                            <div className="shipping-info" xd-qa-type="label" xd-qa-name="regularShippingInfo">
                                The FedEx service guarantee does not apply to packages that are delayed due to causes beyond the control of FedEx, state of emergencies or weather related events. Xpressdocs does not provide shipping credit or refunds for delays beyond the control of FedEx.
                            </div>
                        )
                    }
                </div>

                <div className="box">
                    {store.cart?.items?.length > 1 && !userLevels?.isGuest && (
                        <div className="controls">
                            <div className="break-consolidation">
                                <div className="label" xd-qa-type="label" xd-qa-name="Break Consolidation">
                                    Split Shipping?
                                </div>

                                {SliderCheckbox({
                                    value: !!store.cart.shipping?.breakShippingConsolidation,
                                    onChange: async (splitShipping) => {
                                        breakingConsolidation.onChange(true);
                                        try {
                                            await store.dispatch(setShippingConsolidation(splitShipping, {apis}));
                                        } catch (e) {
                                            alert(e);
                                        } finally {
                                            breakingConsolidation.onChange(false);
                                        }
                                    },
                                    xdQaName: "Break Consolidation"
                                })}
                            </div>
                        </div>
                    )}

                    {cs(
                        keyed(mode.name),
                        ({}) => mode.control.render(),
                    )}

                </div>

                {breakingConsolidation.value && (
                    LoadingPanel({})
                )}
            </div>
        ),
    }),
);

const setShippingConsolidation = (value, {apis}) => ({
    action: async (cart) => {
        const resultCart = await apis.next.cart.updateCart(chain(
            {cartId: cart.cartId},
            (_) => snv(_, ["shipping"], {
                ...omit(cart.shipping, ["itemId"]),
                breakShippingConsolidation: value,
            }),
        ));
        if (resultCart.result === "failure") {
            return {
                error: resultCart.errors?.[0].msg ?? "Update failed",
            };
        } else {
            return {
                change: applyResultCart(resultCart),
            };
        }
    },
});

export const syncShippingAddress = async (cart, {apis, userLevels, getDefaultAddress, apiError}) => await cs(
    ({}, next) => {
        if (!cart.items.find((item) => item.selections?.distributionType === "shipping")) {
            return cart;
        }

        if (cart.shipping && cart.shipping.serviceLevel) {
            return cart;
        }

        return next();
    },
    ["addressId", async (_, next) => {
        if (cart.shipping?.addressId != null) {
            return await next(cart.shipping.addressId);
        }

        if (userLevels.userLevel == AGENT_LEVEL) {
            const myAddresses = await apis.next.shipping.getMyShippingAddresses();
            if (myAddresses[0]) {
                return await next(myAddresses[0].idPrimary);
            }
        }

        const defaultAddress = getDefaultAddress?.();
        if (defaultAddress) {
            return await next(defaultAddress.idPrimary);
        }

        return cart;
    }],
    async ({addressId}) => {

        try {
            const shippingRates = await apis.next.shipping.getConsolidatedShippingRates({
                addressId,
                cartId: cart.cartId,
                isInternalUser: userLevels?.internalUser,
            });
            return await apis.next.cart.updateCart({
                cartId: cart.cartId,
                "shipping": {
                    addressId,
                    "serviceLevel": Object.keys(shippingRates).find((serviceLevel) => serviceLevel === cart.shipping?.serviceLevel) || findMinE(Object.keys(shippingRates), (rate) => +shippingRates[rate].price),
                }
            });
        } catch (error) {

            apiError.onChange(error?.error_code);
            return await apis.next.cart.updateCart({
                cartId: cart.cartId,
                "shipping": {
                    addressId,
                    "serviceLevel": "",
                }
            });
        }
    },
);
